import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Lang } from "@/utils/routes";
import { routes } from "@/utils/routes";

/**
 * Find the route entry that matches the given path
 */
function findRouteForPath(path: string): [string, string] | undefined {
  // Check all main routes
  for (const key in routes) {
    const route = routes[key as keyof typeof routes];

    // Handle nested routes
    if (Array.isArray(route)) {
      if (route[0] === path || route[1] === path) {
        return route as [string, string];
      }
    } else {
      // Search in nested objects (e.g., features, articles)
      for (const nestedKey in route) {
        const nestedRoute = route[nestedKey];
        if (
          Array.isArray(nestedRoute) &&
          (nestedRoute[0] === path || nestedRoute[1] === path)
        ) {
          return nestedRoute as [string, string];
        }
      }
    }
  }

  return undefined;
}

/**
 * Hook for determining current language from the URL and syncing i18n.
 */
export function useLanguage() {
  const location = useLocation();
  const { i18n } = useTranslation();

  const isEnglish = location.pathname.startsWith("/en");
  const language: Lang = isEnglish ? "en" : "sv";

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);

      if (typeof window !== "undefined") {
        localStorage.setItem("lang", language);
      }
    }
  }, [i18n, language]);

  /**
   * Get the alternate language path for the current URL
   * Uses route mapping to ensure correct language equivalent paths
   */
  const getAlternateLink = (currentPath: string): string => {
    // Find matching route in our route map
    const routeTuple = findRouteForPath(currentPath);

    if (routeTuple) {
      // Return the opposite language version
      return isEnglish ? routeTuple[0] : routeTuple[1];
    }

    // Fallback to simple prefix/removal for paths not in our route map
    if (isEnglish) {
      // Remove /en prefix for English to Swedish
      return currentPath.replace(/^\/en/, "") || "/";
    } else {
      // Add /en prefix for Swedish to English
      return `/en${currentPath === "/" ? "" : currentPath}`;
    }
  };

  return { language, isEnglish, getAlternateLink };
}
